import type {
  GetTemplatesRes,
  Template,
  RelatedTemplate,
  TemplateData,
  TemplateDataColumn,
  GetTemplatesReq,
  TemplateListItemRes,
  TemplateVersions,
  KpiTemplate,
} from 'types';
import { heliosApi } from '.';
import { TAG_TYPES } from 'types/services';
import type { TemplateDataColumnExpanded } from 'types/templates';
import { encodeSqlForTransport } from 'utils/helios/sqlTransport';

export type BatchTemplateChildrenItem = {
  templateId: string;
  children: TemplateListItemRes[];
};

export type BatchTemplateChildrenResponse = {
  items: BatchTemplateChildrenItem[];
};

export const heliosTemplatesService = heliosApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getTemplates: builder.query<GetTemplatesRes, GetTemplatesReq>({
      query: ({ page, pageSize, orderBy, orderDesc, search, datasourceId, moduleId, templateType, name, status }) => ({
        url: 'Templates',
        method: 'GET',
        params: {
          Page: page,
          PageSize: pageSize,
          OrderBy: orderBy,
          OrderDesc: orderDesc,
          Search: search,
          DatasourceId: datasourceId,
          ModuleId: moduleId,
          Status: status,
          Name: name,
          ...(templateType && { TemplateType: templateType }),
        },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'LIST' }],
    }),
    getFlatTemplates: builder.query<GetTemplatesRes, GetTemplatesReq>({
      query: ({ page, pageSize, orderBy, orderDesc, search, datasourceId, moduleId, templateType, name, status }) => ({
        url: 'Templates/Flat',
        method: 'GET',
        params: {
          Page: page,
          PageSize: pageSize,
          OrderBy: orderBy,
          OrderDesc: orderDesc,
          Search: search,
          DatasourceId: datasourceId,
          ModuleId: moduleId,
          Status: status,
          Name: name,
          ...(templateType && { TemplateType: templateType }),
        },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'LIST' }],
    }),
    getTemplateById: builder.query<Template, { id: string; suppressErrorToast?: boolean }>({
      query: ({ id }) => ({
        url: `Templates/${id}`,
        method: 'GET',
      }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.id,
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getRelatedTemplatesById: builder.query<RelatedTemplate[], { id: string; type: string }>({
      query: ({ id, type }) => ({
        url: `Templates/${id}/Relateds/${type}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getTemplateListsByTemplateId: builder.query<TemplateListItemRes[], { templateId: string; recordNumber?: string }>({
      query: ({ templateId, recordNumber }) => ({
        url: `TemplateLists/${templateId}`,
        method: 'GET',
        params: {
          ...(recordNumber && { recordNumber }),
        },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'LIST' }],
    }),
    getTemplateLists: builder.query<TemplateListItemRes[], { id: string }>({
      query: ({ id }) => ({
        url: `Templates/${id}/TemplateLists`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getBatchTemplateChildren: builder.query<BatchTemplateChildrenResponse, { templateIds: string[] }>({
      query: ({ templateIds }) => ({
        url: 'Templates/Children',
        method: 'POST',
        body: { templateIds },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    createTemplate: builder.mutation({
      query: (body) => ({
        url: 'Templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'LIST' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),

    updateTemplate: builder.mutation({
      query: (body) => ({
        url: 'Templates',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'LIST' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    draftTemplate: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `TemplateDrafts/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'LIST' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    publishTemplate: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `TemplatePublics/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'LIST' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    copyTemplate: builder.mutation<Template, { id: string; name: string; moduleId: string; datasourceId: string; templateType?: string }>({
      query: ({ id, name, moduleId, datasourceId, templateType }) => ({
        url: `Templates/${id}/Copy`,
        method: 'POST',
        body: {
          name,
          moduleId,
          datasourceId,
          templateType,
        },
      }),
      invalidatesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'LIST' }],
    }),

    removeTemplate: builder.mutation<void, { ids: string[] }>({
      query: ({ ids }) => ({
        url: 'Templates',
        method: 'DELETE',
        params: { ids },
      }),
      invalidatesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'LIST' }],
    }),

    getTemplateDataModelById: builder.query<TemplateData, { id: string }>({
      query: ({ id }) => ({
        url: `Templates/${id}/TemplateData`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getTemplateDataColumnsById: builder.query<TemplateDataColumn[], { id: string; type?: string[] }>({
      query: ({ id, type }) => ({
        url: `Templates/${id}/TemplateData/Columns`,
        method: 'GET',
        params: {
          dataType: type,
        },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getTemplateDataColumnsExpandedById: builder.query<TemplateDataColumnExpanded, { id: string; type?: string[] }>({
      query: ({ id, type }) => ({
        url: `Templates/${id}/TemplateData/ColumnsExpanded`,
        method: 'GET',
        params: {
          dataType: type,
        },
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getTemplateSqlParameters: builder.query<{ sqlQueryParameters: string[] }, unknown>({
      query: () => ({
        url: `TemplateSqlParameters`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),

    updateTemplateDataModelById: builder.mutation<void, { id: string; dataModel: string }>({
      query: ({ id, dataModel }) => ({
        url: `Templates/${id}/TemplateData`,
        method: 'PUT',
        body: { sqlQuery: encodeSqlForTransport(dataModel) },
      }),
      invalidatesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    getTemplateVersions: builder.query<TemplateVersions, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `TemplateVersions/${templateId}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'DETAIL' }],
    }),
    createNewTemplateVersion: builder.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `TemplateVersions/${templateId}`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'LIST' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    exportTemplate: builder.query({
      query: ({ templateId, relatedTemplates }: { templateId: string; relatedTemplates: boolean }) => ({
        url: `ExportTemplates/${templateId}`,
        method: 'GET',
        params: { relatedTemplates },
      }),
    }),
    getUnreadNotificationsCountByTemplateIds: builder.query<Record<string, number>, string[]>({
      query: (templateIds) => ({
        url: 'Templates/Notifications/UnreadCount',
        method: 'POST',
        body: templateIds,
      }),
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
    getKpiTemplatesByModuleId: builder.query<KpiTemplate[], string | undefined>({
      query: (moduleId) => ({
        url: `Templates/Modules/${moduleId}/Kpi`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.DATA_HELIOS],
    }),
  }),
});
export const {
  useGetTemplatesQuery,
  useGetFlatTemplatesQuery,
  useGetTemplateByIdQuery,
  useLazyGetTemplateByIdQuery,
  useGetTemplateDataModelByIdQuery,
  useGetTemplateDataColumnsByIdQuery,
  useGetTemplateDataColumnsExpandedByIdQuery,
  useLazyGetTemplateDataColumnsByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useCopyTemplateMutation,
  useRemoveTemplateMutation,
  useGetTemplateSqlParametersQuery,
  useUpdateTemplateDataModelByIdMutation,
  useGetRelatedTemplatesByIdQuery,
  useGetTemplateListsQuery,
  useGetBatchTemplateChildrenQuery,
  useDraftTemplateMutation,
  usePublishTemplateMutation,
  useGetTemplateVersionsQuery,
  useCreateNewTemplateVersionMutation,
  useGetTemplateListsByTemplateIdQuery,
  useGetKpiTemplatesByModuleIdQuery,
  useLazyExportTemplateQuery,
  useGetUnreadNotificationsCountByTemplateIdsQuery,
} = heliosTemplatesService;
